import { useEffect, useRef } from 'react';
import maplibregl, { Map as MaplibreMap, Marker, Popup } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { OSM_STYLE_URL, transformRequest, transformStyle } from '@/lib/mapStyle';

export interface MarkerDef {
  lat: number;
  lon: number;
  label?: string;
  /** Hex (e.g. `#dc2626`). Defaults to alert-red. */
  color?: string;
}

export interface AlertMapEmbedProps {
  markers: MarkerDef[];
  /** CSS height. Default 320px. */
  height?: string;
  /** Override center; otherwise auto-derived from markers (or Johannesburg). */
  centerLat?: number;
  centerLon?: number;
  /** Override zoom; otherwise auto-fit to markers (>=2) or zoom 14 (1). */
  zoom?: number;
}

const DEFAULT_COLOR = '#dc2626';
const FALLBACK_CENTER: [number, number] = [28.0, -26.2]; // Johannesburg

/**
 * Minimal MapLibre embed for the Alert Detail page (and any other place that
 * just needs to drop a few pins on the OSM basemap). Reuses the same OSM
 * style + transform helpers as the full Maps page so behaviour stays in
 * lock-step.
 *
 * The marker list is keyed by JSON identity in the dependency array — if the
 * caller passes a stable reference (or a memoised one), the map instance is
 * built once on mount and only the marker layer is rebuilt when the list
 * changes.
 */
export default function AlertMapEmbed({
  markers,
  height = '320px',
  centerLat,
  centerLon,
  zoom,
}: AlertMapEmbedProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markersRef = useRef<Marker[]>([]);

  // Init map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // Pick an initial center: explicit prop > marker average > Johannesburg.
    let initialCenter: [number, number] = FALLBACK_CENTER;
    if (centerLon != null && centerLat != null) {
      initialCenter = [centerLon, centerLat];
    } else if (markers.length > 0) {
      const avgLon = markers.reduce((s, m) => s + m.lon, 0) / markers.length;
      const avgLat = markers.reduce((s, m) => s + m.lat, 0) / markers.length;
      initialCenter = [avgLon, avgLat];
    }
    const initialZoom = zoom ?? (markers.length === 1 ? 14 : 6);

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: { version: 8, sources: {}, layers: [] },
      center: initialCenter,
      zoom: initialZoom,
      transformRequest,
      attributionControl: { compact: true },
    });
    map.setStyle(OSM_STYLE_URL, { transformStyle });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    // Watch container size — the section card may resize after mount.
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers + bounds whenever the marker list changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (markers.length === 0) return;

    for (const m of markers) {
      const el = document.createElement('div');
      el.style.cssText =
        `width:14px;height:14px;border-radius:50%;background:${m.color ?? DEFAULT_COLOR};` +
        'border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.45);cursor:pointer';
      const marker = new Marker({ element: el }).setLngLat([m.lon, m.lat]);
      if (m.label) {
        marker.setPopup(
          new Popup({ offset: 14 }).setHTML(
            `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px;font-weight:600">${escHtml(m.label)}</div>`,
          ),
        );
      }
      marker.addTo(map);
      markersRef.current.push(marker);
    }

    if (markers.length === 1) {
      map.flyTo({ center: [markers[0].lon, markers[0].lat], zoom: zoom ?? 14, duration: 400 });
    } else if (markers.length >= 2) {
      const lons = markers.map((m) => m.lon);
      const lats = markers.map((m) => m.lat);
      map.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        { padding: 48, duration: 400, maxZoom: 15 },
      );
    }
  }, [markers, zoom]);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-md border"
      style={{ height }}
    />
  );
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
